import { useCallback, useSyncExternalStore } from 'react';
import { CONSENT_IDS } from '../../shared/consent';
import type { ConsentId, ConsentRecord } from '../../shared/consent';
import { isSupportedLanguage } from '../../shared/language';
import type { ClientRow } from '../../shared/queries';
import { forgetSentMessages } from './presence';
import { forgetHurtAnswers } from './progress';
import { forgetMeasurements } from './measurements';

/**
 * What the client has agreed to, what they have changed their mind about, and
 * the append-only record of every one of those decisions — held **on this device
 * and nowhere else**.
 *
 * ## Why it is on the device, which is not a preference
 *
 * `client` already carries `ack_version`, `ack_at`, `consent_metrics_*`,
 * `consent_messaging_*` and `consent_hurt_*` from `00002_people.sql`, and this
 * screen reads them. What it cannot do is **write** them: the column grant in
 * `00005_policies.sql` is real, but the only update policy on `client` is
 * `client_update_coach`. There is no `client_update_self`, so a client's own
 * request to change a consent is refused by row-level security — and
 * `client-me`'s design lists that missing policy under Non-Goals, along with
 * every other migration.
 *
 * So the client's own choices are held here, overlaying the row, exactly the way
 * `./progress.ts` holds a tick and `./presence.ts` holds a message. **The
 * screens say so**, on the first-run screen and again on the profile screen, in
 * the ordinary weight and with nothing asked of the client. Two consequences
 * that a client will meet and should therefore be told rather than discover:
 *
 *   * a choice made here **does not reach the coach**, so a feature she can see
 *     the state of in her own admin is not the feature the client is using;
 *   * **another phone asks again**, including for the acknowledgement, because
 *     this browser is the only thing that remembers.
 *
 * Both are on the gate for the first real client.
 *
 * ## The journal, and why it is append-only today rather than one day
 *
 * `legal-review-at-the-gate`'s carve-out requires that a consent record be
 * append-only, one row per decision, with withdrawal recorded as a new decision
 * rather than an edit. That is a shape that cannot be retrofitted — you cannot
 * reconstruct what somebody read in 2026 — so it is built now even though the
 * table it will live in does not exist: `records` is only ever appended to, is
 * never reordered, is never edited, and has no removal function at all. There is
 * deliberately **no `editRecord`, no `clearRecords` and no `id` to address one
 * by**.
 *
 * **What this journal is not.** It is not the legal record. It is in a browser,
 * it is lost with site data, and it reaches nobody. It is the *shape* of the
 * record, exercised by real screens so that the day there is a table, the rows
 * going into it are the rows this module already builds. `src/shared/consent.ts`
 * holds that shape and the reasoning behind every field.
 *
 * ## Withdrawal deletes, here, for real
 *
 * `client-me`'s spec requires that turning a consent off deletes the content
 * stored under it. On this device that content genuinely exists, so this module
 * genuinely deletes it: measurements, the messages the client typed, and every
 * answer to "did anything hurt". That part is not a stand-in.
 */

/** The device's opinion about one consent: when it was given, or `null`. */
interface Decided {
  at: string | null;
}

interface Choices {
  /** Only the consents this device has an opinion about. */
  decided: Partial<Record<ConsentId, Decided>>;
  /** Every decision, oldest first. Appended to and never otherwise touched. */
  records: ConsentRecord[];
}

const EMPTY: Choices = { decided: {}, records: [] };

/** One key, with a version in it, like every other device store in this app. */
const STORAGE_KEY = 'client.consentChoices.v1';

function isConsentId(value: unknown): value is ConsentId {
  return typeof value === 'string' && (CONSENT_IDS as readonly string[]).includes(value);
}

function isRecord(value: unknown): value is ConsentRecord {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Partial<ConsentRecord>;
  return (
    isConsentId(row.consent) &&
    (row.decision === 'given' || row.decision === 'declined' || row.decision === 'withdrawn') &&
    typeof row.text === 'string' &&
    isSupportedLanguage(row.language) &&
    typeof row.textVersion === 'string' &&
    (row.surface === 'first-run' || row.surface === 'profile') &&
    typeof row.at === 'string'
  );
}

function parse(raw: string): Choices {
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return EMPTY;
    const row = value as Partial<Choices>;
    const decided: Partial<Record<ConsentId, Decided>> = {};
    for (const [id, state] of Object.entries(row.decided ?? {})) {
      if (!isConsentId(id)) continue;
      const at = (state as Partial<Decided> | undefined)?.at;
      if (at === null || typeof at === 'string') decided[id] = { at: at ?? null };
    }
    return {
      decided,
      records: Array.isArray(row.records) ? row.records.filter(isRecord) : [],
    };
  } catch {
    return EMPTY;
  }
}

let cachedRaw: string | null = null;
let cached: Choices = EMPTY;

function readRaw(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Everything this device holds. Never null; an untouched device has decided nothing. */
export function readChoices(): Choices {
  const raw = readRaw();
  if (raw === null) return EMPTY;
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cached = parse(raw);
  }
  return cached;
}

const listeners = new Set<() => void>();

function write(next: Choices): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A browser that refuses to remember asks again. It is not a reason to fail
    // the screen somebody is standing in front of.
  }
  cachedRaw = null;
  for (const listener of listeners) listener();
}

/**
 * Apply one decision and keep the record of it.
 *
 * The record is built by the caller from the words that were on screen
 * (`buildConsentRecord`), so what is kept is what was read. Both halves happen
 * together, in one write, because a state without its record is the exact
 * failure the carve-out exists to prevent.
 *
 * **Withdrawing deletes.** A decision of `withdrawn` removes whatever this
 * device holds under that consent, immediately and before the screen re-renders,
 * so that turning it back on returns an empty feature rather than the old one.
 */
export function decide(records: readonly ConsentRecord[]): void {
  const current = readChoices();
  const decided = { ...current.decided };

  for (const record of records) {
    decided[record.consent] = { at: record.decision === 'given' ? record.at : null };
    if (record.decision === 'withdrawn') erase(record.consent);
  }

  write({ decided, records: [...current.records, ...records] });
}

/** What withdrawing one consent actually removes from this device. */
function erase(consent: ConsentId): void {
  if (consent === 'metrics') forgetMeasurements();
  if (consent === 'messaging') forgetSentMessages();
  if (consent === 'hurt') forgetHurtAnswers();
  // `ack` and `age` are required and cannot be withdrawn from inside the
  // product: withdrawing the acknowledgement is leaving, which is the deletion
  // request on the profile screen and not a switch.
}

/** Forget every choice this device holds. Used by tests, and by nothing else. */
export function forgetChoices(): void {
  write(EMPTY);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** What this device holds, re-rendered when it changes anywhere in this tab. */
export function useChoices(): Choices {
  const getSnapshot = useCallback(() => readChoices(), []);
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}

/**
 * When a consent was given — the device's answer if it has one, otherwise the
 * database row's. `null` means it is not given.
 *
 * The device wins because it is the more recent decision by construction: a row
 * can only have been written by the coach's side, and the client's own change is
 * always later than the state it changed.
 *
 * **Age has no column**, on purpose: the carve-out stores the confirmation and
 * never a date of birth or an age, and `00002_people.sql` predates it. So it is
 * device-only until there is a migration, which is stated rather than hidden.
 */
export function consentGivenAt(
  consent: ConsentId,
  row: ClientRow | null | undefined,
  choices: Choices,
): string | null {
  const device = choices.decided[consent];
  if (device !== undefined) return device.at;
  if (!row) return null;
  switch (consent) {
    case 'ack':
      return row.ack_at;
    case 'metrics':
      return row.consent_metrics_at;
    case 'messaging':
      return row.consent_messaging_at;
    case 'hurt':
      return row.consent_hurt_at;
    case 'age':
      return null;
  }
}

/** Whether a consent is currently on. */
export function hasConsent(
  consent: ConsentId,
  row: ClientRow | null | undefined,
  choices: Choices,
): boolean {
  return consentGivenAt(consent, row, choices) !== null;
}

/**
 * Whether the client may be shown a plan, a session or a note.
 *
 * Both required consents, and nothing else. It is one function so that there is
 * one answer: `ClientGuard` is the only enforcement point, and a second opinion
 * somewhere else is how a screen ends up reachable before the acknowledgement.
 */
export function mayEnter(row: ClientRow | null | undefined, choices: Choices): boolean {
  return hasConsent('ack', row, choices) && hasConsent('age', row, choices);
}
